import { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { copyToClipboard, generateShareableLink, generateShareText, shareViaWebShare } from '../../utils/shareableLink';
import type { Event } from '../../types';

interface ShareableLinkDisplayProps {
  event: Event;
  showCopyButton?: boolean;
  showShareButton?: boolean;
  compact?: boolean;
}

/**
 * Shareable Link Display Component
 * Displays shareable link with copy and share functionality
 */
export function ShareableLinkDisplay({
  event,
  showCopyButton = true,
  showShareButton = true,
  compact = false,
}: ShareableLinkDisplayProps) {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  const shareableLink = generateShareableLink(event.id);

  const handleCopy = async () => {
    const success = await copyToClipboard(shareableLink);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      // Fallback: show link in alert
      alert(`Shareable link: ${shareableLink}`);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    const shareText = generateShareText(event.name, event.id);
    const shared = await shareViaWebShare(event.name, shareText, shareableLink);
    
    if (!shared) {
      // Web Share API not available, fallback to copy
      await handleCopy();
    }
    
    setSharing(false);
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={shareableLink}
            readOnly
            className="w-full px-3 py-2 bg-tertiary-bg border border-border rounded-lg text-sm text-primary-text"
            onClick={(e) => (e.target as HTMLInputElement).select()}
          />
        </div>
        {showCopyButton && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCopy}
            disabled={copied}
          >
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        )}
      </div>
    );
  }

  return (
    <Card padding="md">
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-primary-text mb-2">
            Shareable Link
          </h3>
          <p className="text-sm text-secondary-text mb-3">
            Share this link with participants to join your event
          </p>
        </div>

        <div className="bg-tertiary-bg rounded-lg p-3 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <code className="flex-1 text-sm text-primary-text break-all">
              {shareableLink}
            </code>
            {copied && (
              <Badge variant="success" size="sm">
                Copied!
              </Badge>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          {showCopyButton && (
            <Button
              variant="primary"
              onClick={handleCopy}
              disabled={copied}
              fullWidth={!showShareButton}
            >
              {copied ? '✓ Copied!' : 'Copy Link'}
            </Button>
          )}
          {showShareButton && (
            <Button
              variant="secondary"
              onClick={handleShare}
              disabled={sharing}
              fullWidth={!showCopyButton}
            >
              {sharing ? 'Sharing...' : 'Share'}
            </Button>
          )}
        </div>

        <div className="text-xs text-muted-text space-y-1">
          <p>Participants can paste this link to join your event</p>
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-sm text-secondary-text mb-2">Public View:</p>
            <a
              href={`/screencast/${event.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-sm text-primary-blue hover:underline"
            >
              📺 Screencast Leaderboard
            </a>
          </div>
          <div className="p-2 bg-warning-yellow/10 border border-warning-yellow/30 rounded mt-2">
            <p className="text-warning-yellow font-medium text-xs">
              ⚠️ Important: Events are stored locally in your browser. Links only work in the same browser/device where the event was created. For sharing across different browsers/devices, a backend server is required.
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}

